import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  notifications: [], // Array of notification objects
  nextId: 1
};

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // Add a new notification
    addNotification: (state, action) => {
      const { type, message, isInline = true, autoClose = true, duration = 5000 } = action.payload;
      
      const notification = {
        id: state.nextId++,
        type,
        message,
        isInline,
        autoClose,
        duration,
        timestamp: Date.now()
      };
      
      // Add to beginning so latest notifications appear first
      state.notifications.unshift(notification);
      
      // Limit to 5 notifications max to prevent memory issues
      if (state.notifications.length > 5) {
        state.notifications = state.notifications.slice(0, 5);
      }
    },
    
    // Remove a specific notification by ID
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      state.notifications = state.notifications.filter(n => n.id !== notificationId);
    },
    
    // Clear all notifications
    clearAllNotifications: (state) => {
      state.notifications = [];
    },
    
    // Helper for error notifications
    addErrorNotification: (state, action) => {
      const { message, isInline = true } = action.payload;
      const notification = {
        id: state.nextId++,
        type: 'alert-high',
        message,
        isInline,
        autoClose: true,
        duration: 8000, // Longer for errors
        timestamp: Date.now()
      };
      state.notifications.unshift(notification);
      if (state.notifications.length > 5) {
        state.notifications = state.notifications.slice(0, 5);
      }
    },
    
    // Helper for success notifications
    addSuccessNotification: (state, action) => {
      const { message, isInline = true } = action.payload;
      const notification = {
        id: state.nextId++,
        type: 'success',
        message,
        isInline,
        autoClose: true,
        duration: 4000, // Shorter for success
        timestamp: Date.now()
      };
      state.notifications.unshift(notification);
      if (state.notifications.length > 5) {
        state.notifications = state.notifications.slice(0, 5);
      }
    },
    
    // Helper for info notifications
    addInfoNotification: (state, action) => {
      const { message, isInline = true } = action.payload;
      const notification = {
        id: state.nextId++,
        type: 'alert-low',
        message,
        isInline,
        autoClose: true,
        duration: 5000,
        timestamp: Date.now()
      };
      state.notifications.unshift(notification);
      if (state.notifications.length > 5) {
        state.notifications = state.notifications.slice(0, 5);
      }
    },
    
    // Helper for warning notifications
    addWarningNotification: (state, action) => {
      const { message, isInline = true } = action.payload;
      const notification = {
        id: state.nextId++,
        type: 'alert-medium',
        message,
        isInline,
        autoClose: true,
        duration: 6000,
        timestamp: Date.now()
      };
      state.notifications.unshift(notification);
      if (state.notifications.length > 5) {
        state.notifications = state.notifications.slice(0, 5);
      }
    }
  }
});

export const { 
  addNotification, 
  removeNotification, 
  clearAllNotifications,
  addErrorNotification,
  addSuccessNotification, 
  addInfoNotification,
  addWarningNotification
} = notificationSlice.actions;

// Selectors
export const selectNotifications = (state) => state.notifications.notifications;
export const selectLatestNotification = (state) => 
  state.notifications.notifications.length > 0 ? state.notifications.notifications[0] : null;

export default notificationSlice.reducer;